import Konva from "konva";
import { resolveFxBlendComposite, type FxRenderPacket } from "./fxRuntime";
import type { FxOverlayVideoLayer } from "./fxTypes";

export const FX_OVERLAY_CANVAS_EDIT_EVENT = "vb:fx-overlay-canvas-edit-target";

const BATCH_KEY_SUFFIX = "__batch";
const MIN_BATCH_RENDER_SCALE = 0.5;
const MAX_BATCH_RENDER_SCALE = 1;

export type FxVideoOverlayCanvasEditTarget = {
  fxBlockId: string;
  layerId: string;
} | null;

export type FxVideoOverlayCanvasEditCommit = {
  fxBlockId: string;
  layerId: string;
  positionX: number;
  positionY: number;
};

type LegacyFxVideoOverlayEntry = {
  kind: "legacy";
  key: string;
  elementId: string;
  fxBlockId: string;
  layerId: string;
  src: string;
  sourceNode: Konva.Node;
  overlayNode: Konva.Image;
  video: HTMLVideoElement;
  metadataLoaded: boolean;
  failed: boolean;
  warned: boolean;
  size: { width: number; height: number };
  dragging: boolean;
  editBound: boolean;
  currentLayer: FxOverlayVideoLayer;
  onCanvasEditCommit: ((next: FxVideoOverlayCanvasEditCommit) => void) | null;
  onDragStart: () => void;
  onDragEnd: () => void;
  onLoadedMetadata: () => void;
  onError: () => void;
};

type FxVideoBatchLayerEntry = {
  key: string;
  fxBlockId: string;
  layerId: string;
  src: string;
  video: HTMLVideoElement;
  metadataLoaded: boolean;
  failed: boolean;
  warned: boolean;
  currentLayer: FxOverlayVideoLayer;
  onLoadedMetadata: () => void;
  onError: () => void;
};

type BatchFxVideoOverlayEntry = {
  kind: "batch";
  key: string;
  elementId: string;
  sourceNode: Konva.Node;
  overlayNode: Konva.Image;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  size: { width: number; height: number };
  renderScale: number;
  layers: Map<string, FxVideoBatchLayerEntry>;
};

export type FxVideoOverlayEntry = LegacyFxVideoOverlayEntry | BatchFxVideoOverlayEntry;
export type FxVideoOverlayMap = Map<string, FxVideoOverlayEntry>;

export type FxVideoOverlaySyncResult = {
  activeKeys: Set<string>;
  changed: boolean;
  requestedLayerCount: number;
  renderedLayerCount: number;
  droppedLayerCount: number;
  compositorPasses: number;
};

type OverlayLayerCandidate = {
  key: string;
  packet: FxRenderPacket;
  layer: FxOverlayVideoLayer;
};

type BatchDrawCandidate = {
  key: string;
  order: number;
  layerEntry: FxVideoBatchLayerEntry;
  opacity: number;
  zOffset: number;
  placementMode: FxOverlayVideoLayer["placementMode"];
};

export function syncFxVideoOverlays(args: {
  elementId: string;
  node: Konva.Node;
  packets: FxRenderPacket[];
  overlayMap: FxVideoOverlayMap;
  canvasEditTarget?: FxVideoOverlayCanvasEditTarget;
  onCanvasEditCommit?: (next: FxVideoOverlayCanvasEditCommit) => void;
  renderScale?: number;
  maxRenderedLayers?: number | null;
  isPlaying?: boolean;
}): FxVideoOverlaySyncResult {
  const {
    elementId,
    node,
    packets,
    overlayMap,
    canvasEditTarget = null,
    onCanvasEditCommit,
    renderScale = 1,
    maxRenderedLayers = null,
    isPlaying = false,
  } = args;

  const overlays = collectOverlayLayers(packets, elementId);
  if (overlays.length === 0) {
    return {
      activeKeys: new Set<string>(),
      changed: false,
      requestedLayerCount: 0,
      renderedLayerCount: 0,
      droppedLayerCount: 0,
      compositorPasses: 0,
    };
  }

  // Canvas placement mode targets a specific layer drag handle.
  // Preserve per-layer overlay nodes in this mode.
  const hasCanvasEditTarget = !!canvasEditTarget;
  const hasScreenSpaceLayer = overlays.some((candidate) => candidate.layer.placementMode === "screen-space");
  const canBatch = !hasCanvasEditTarget && !hasScreenSpaceLayer;

  if (canBatch) {
    return syncFxVideoOverlaysBatched({
      elementId,
      node,
      overlays,
      overlayMap,
      renderScale,
      maxRenderedLayers,
      isPlaying,
    });
  }

  return syncFxVideoOverlaysLegacy({
    elementId,
    node,
    overlays,
    overlayMap,
    canvasEditTarget,
    onCanvasEditCommit,
    maxRenderedLayers,
    isPlaying,
  });
}

function syncFxVideoOverlaysLegacy(args: {
  elementId: string;
  node: Konva.Node;
  overlays: OverlayLayerCandidate[];
  overlayMap: FxVideoOverlayMap;
  canvasEditTarget: FxVideoOverlayCanvasEditTarget;
  onCanvasEditCommit?: (next: FxVideoOverlayCanvasEditCommit) => void;
  maxRenderedLayers?: number | null;
  isPlaying?: boolean;
}): FxVideoOverlaySyncResult {
  const {
    elementId,
    node,
    overlays,
    overlayMap,
    canvasEditTarget,
    onCanvasEditCommit,
    maxRenderedLayers = null,
    isPlaying = false,
  } = args;
  const activeKeys = new Set<string>();
  const parent = node.getParent();
  if (!parent) {
    return {
      activeKeys,
      changed: false,
      requestedLayerCount: 0,
      renderedLayerCount: 0,
      droppedLayerCount: 0,
      compositorPasses: 0,
    };
  }

  // Editing/screen-space path must not keep stale batch entry alive.
  let changed = removeFxVideoOverlay(getBatchOverlayKey(elementId), overlayMap);
  let compositorPasses = 0;
  let anchor: Konva.Node = node;
  const activeCandidates: Array<{
    key: string;
    order: number;
    packet: FxRenderPacket;
    layer: FxOverlayVideoLayer;
    entry: LegacyFxVideoOverlayEntry;
    mergedOpacity: number;
    isCanvasEditTarget: boolean;
    zOffset: number;
  }> = [];

  for (let order = 0; order < overlays.length; order += 1) {
    const candidate = overlays[order];
    const key = candidate.key;
    activeKeys.add(key);
    const size = resolveNodeSize(node, candidate.layer);
    if (size.width <= 0 || size.height <= 0) continue;

    const entry = ensureLegacyOverlayEntry({
      key,
      elementId,
      packet: candidate.packet,
      layer: candidate.layer,
      node,
      parent,
      overlayMap,
      size,
    });
    if (!entry || entry.failed) continue;

    const isCanvasEditTarget =
      !!canvasEditTarget &&
      canvasEditTarget.fxBlockId === candidate.packet.fxBlockId &&
      canvasEditTarget.layerId === candidate.layer.id;
    changed =
      configureOverlayCanvasEdit({
        entry,
        enabled: isCanvasEditTarget,
        layer: candidate.layer,
        onCanvasEditCommit,
      }) || changed;

    const playback = resolveOverlayPlaybackTime({
      packet: candidate.packet,
      layer: candidate.layer,
      videoDuration: entry.video.duration,
    });

    if (!playback.active) {
      changed = hideOverlayNode(entry.overlayNode) || changed;
      pauseOverlayVideo(entry.video);
      continue;
    }

    const mergedOpacity = clamp01(
      toFinite(candidate.packet.opacity, 1) * clamp01(toFinite(candidate.layer.opacity, 1))
    );
    changed =
      syncOverlayVideoPlayback({
        video: entry.video,
        metadataLoaded: entry.metadataLoaded,
        playbackTimeSec: playback.timeSec,
        playbackRate: clamp(toFinite(candidate.layer.playbackRate, 1), 0.1, 4),
        isPlaying,
      }) || changed;

    activeCandidates.push({
      key,
      order,
      packet: candidate.packet,
      layer: candidate.layer,
      entry,
      mergedOpacity,
      isCanvasEditTarget,
      zOffset: resolveOverlayZIndexOffset(candidate.layer),
    });
  }

  const renderableKeys = selectOverlayKeysToRender(
    activeCandidates.map((candidate) => ({
      key: candidate.key,
      order: candidate.order,
      zOffset: candidate.zOffset,
      opacity: candidate.mergedOpacity,
      placementMode: candidate.layer.placementMode,
    })),
    maxRenderedLayers
  );

  for (const candidate of activeCandidates) {
    if (!renderableKeys.has(candidate.key)) {
      changed = hideOverlayNode(candidate.entry.overlayNode) || changed;
      pauseOverlayVideo(candidate.entry.video);
      continue;
    }

    if (!(candidate.isCanvasEditTarget && candidate.entry.dragging)) {
      syncOverlayTransform(candidate.entry.overlayNode, node, candidate.layer);
    }
    placeOverlayAbove({
      parent,
      source: anchor,
      overlay: candidate.entry.overlayNode,
      zOffset: candidate.zOffset,
    });
    anchor = candidate.entry.overlayNode;

    if (!candidate.entry.overlayNode.visible()) {
      candidate.entry.overlayNode.visible(true);
      changed = true;
    }

    if (Math.abs(candidate.entry.overlayNode.opacity() - candidate.mergedOpacity) > 0.0001) {
      candidate.entry.overlayNode.opacity(candidate.mergedOpacity);
      changed = true;
    }

    const composite = resolveFxBlendComposite(candidate.layer.blendMode);
    if (candidate.entry.overlayNode.globalCompositeOperation() !== composite) {
      candidate.entry.overlayNode.globalCompositeOperation(composite);
      changed = true;
    }

    compositorPasses += 1;
  }

  return {
    activeKeys,
    changed,
    requestedLayerCount: activeCandidates.length,
    renderedLayerCount: renderableKeys.size,
    droppedLayerCount: Math.max(0, activeCandidates.length - renderableKeys.size),
    compositorPasses,
  };
}

function syncFxVideoOverlaysBatched(args: {
  elementId: string;
  node: Konva.Node;
  overlays: OverlayLayerCandidate[];
  overlayMap: FxVideoOverlayMap;
  renderScale: number;
  maxRenderedLayers?: number | null;
  isPlaying?: boolean;
}): FxVideoOverlaySyncResult {
  const { elementId, node, overlays, overlayMap, renderScale, maxRenderedLayers = null, isPlaying = false } = args;
  const activeKeys = new Set<string>();
  const batchKey = getBatchOverlayKey(elementId);
  activeKeys.add(batchKey);
  const parent = node.getParent();
  if (!parent) {
    return {
      activeKeys,
      changed: false,
      requestedLayerCount: 0,
      renderedLayerCount: 0,
      droppedLayerCount: 0,
      compositorPasses: 0,
    };
  }

  let changed = cleanupElementLegacyOverlays(elementId, overlayMap);
  const size = resolveNodeSize(node, overlays[0].layer);
  if (size.width <= 0 || size.height <= 0) {
    return {
      activeKeys,
      changed,
      requestedLayerCount: 0,
      renderedLayerCount: 0,
      droppedLayerCount: 0,
      compositorPasses: 0,
    };
  }

  const entry = ensureBatchOverlayEntry({
    key: batchKey,
    elementId,
    node,
    parent,
    overlayMap,
    size,
    renderScale,
  });
  if (!entry) {
    return {
      activeKeys,
      changed,
      requestedLayerCount: 0,
      renderedLayerCount: 0,
      droppedLayerCount: 0,
      compositorPasses: 0,
    };
  }

  const activeLayerKeys = new Set<string>();
  const drawCandidates: BatchDrawCandidate[] = [];
  let highestZOffset = 0;

  for (let order = 0; order < overlays.length; order += 1) {
    const candidate = overlays[order];
    activeLayerKeys.add(candidate.key);
    const layerEntry = ensureBatchLayerEntry(entry, candidate);
    if (!layerEntry || layerEntry.failed) continue;

    const playback = resolveOverlayPlaybackTime({
      packet: candidate.packet,
      layer: candidate.layer,
      videoDuration: layerEntry.video.duration,
    });
    if (!playback.active) {
      pauseOverlayVideo(layerEntry.video);
      continue;
    }

    changed =
      syncOverlayVideoPlayback({
        video: layerEntry.video,
        metadataLoaded: layerEntry.metadataLoaded,
        playbackTimeSec: playback.timeSec,
        playbackRate: clamp(toFinite(candidate.layer.playbackRate, 1), 0.1, 4),
        isPlaying,
      }) || changed;

    // Skip if not decoded yet, or if a seek is in flight after re-entry —
    // drawing while seeking shows the stale frame from the previous playhead position.
    const readyToDraw = (layerEntry.metadataLoaded || layerEntry.video.readyState >= 2) && !layerEntry.video.seeking;
    if (!readyToDraw) continue;

    const mergedOpacity = clamp01(
      toFinite(candidate.packet.opacity, 1) * clamp01(toFinite(candidate.layer.opacity, 1))
    );
    drawCandidates.push({
      key: candidate.key,
      order,
      layerEntry,
      opacity: mergedOpacity,
      zOffset: resolveOverlayZIndexOffset(candidate.layer),
      placementMode: candidate.layer.placementMode,
    });
  }

  changed = cleanupBatchLayers(entry, activeLayerKeys) || changed;

  const renderableKeys = selectOverlayKeysToRender(
    drawCandidates.map((candidate) => ({
      key: candidate.key,
      order: candidate.order,
      zOffset: candidate.zOffset,
      opacity: candidate.opacity,
      placementMode: candidate.placementMode,
    })),
    maxRenderedLayers
  );
  const selectedDrawCandidates = drawCandidates.filter((candidate) => renderableKeys.has(candidate.key));
  for (const candidate of drawCandidates) {
    if (!renderableKeys.has(candidate.key)) {
      pauseOverlayVideo(candidate.layerEntry.video);
    } else {
      highestZOffset = Math.max(highestZOffset, candidate.zOffset);
    }
  }

  if (selectedDrawCandidates.length === 0) {
    changed = hideOverlayNode(entry.overlayNode) || changed;
    return {
      activeKeys,
      changed,
      requestedLayerCount: drawCandidates.length,
      renderedLayerCount: 0,
      droppedLayerCount: drawCandidates.length,
      compositorPasses: 0,
    };
  }

  drawBatchOverlay(entry, selectedDrawCandidates);
  entry.overlayNode.image(entry.canvas);
  entry.overlayNode.width(size.width);
  entry.overlayNode.height(size.height);
  syncBatchOverlayTransform(entry.overlayNode, node);
  entry.overlayNode.opacity(1);
  entry.overlayNode.globalCompositeOperation("source-over");
  if (!entry.overlayNode.visible()) {
    entry.overlayNode.visible(true);
  }
  placeOverlayAbove({
    parent,
    source: node,
    overlay: entry.overlayNode,
    zOffset: highestZOffset,
  });

  return {
    activeKeys,
    changed: true,
    requestedLayerCount: drawCandidates.length,
    renderedLayerCount: selectedDrawCandidates.length,
    droppedLayerCount: Math.max(0, drawCandidates.length - selectedDrawCandidates.length),
    compositorPasses: 1,
  };
}

export function cleanupFxVideoOverlays(args: {
  overlayMap: FxVideoOverlayMap;
  keepKeys: Set<string>;
}): boolean {
  const { overlayMap, keepKeys } = args;
  let changed = false;
  for (const key of overlayMap.keys()) {
    if (keepKeys.has(key)) continue;
    changed = removeFxVideoOverlay(key, overlayMap) || changed;
  }
  return changed;
}

export function disposeFxVideoOverlays(overlayMap: FxVideoOverlayMap): boolean {
  let changed = false;
  for (const key of overlayMap.keys()) {
    changed = removeFxVideoOverlay(key, overlayMap) || changed;
  }
  return changed;
}

function collectOverlayLayers(
  packets: FxRenderPacket[],
  elementId: string
): OverlayLayerCandidate[] {
  const out: OverlayLayerCandidate[] = [];
  for (const packet of packets) {
    const layers = Array.isArray(packet.layers) ? packet.layers : [];
    for (const layer of layers) {
      if (!isOverlayVideoLayer(layer)) continue;
      const key = `${elementId}::${packet.fxBlockId}::${layer.id}`;
      out.push({ key, packet, layer });
    }
  }
  return out;
}

function selectOverlayKeysToRender(
  candidates: Array<{
    key: string;
    order: number;
    zOffset: number;
    opacity: number;
    placementMode: FxOverlayVideoLayer["placementMode"];
  }>,
  maxRenderedLayers: number | null | undefined
): Set<string> {
  if (typeof maxRenderedLayers !== "number" || !Number.isFinite(maxRenderedLayers)) {
    return new Set(candidates.map((candidate) => candidate.key));
  }

  const limit = Math.max(0, Math.floor(Number(maxRenderedLayers)));
  if (limit <= 0) return new Set<string>();
  if (candidates.length <= limit) {
    return new Set(candidates.map((candidate) => candidate.key));
  }

  const selected = candidates
    .slice()
    .sort((a, b) => {
      if (a.placementMode !== b.placementMode) {
        return a.placementMode === "screen-space" ? -1 : 1;
      }
      if (a.zOffset !== b.zOffset) return b.zOffset - a.zOffset;
      if (Math.abs(a.opacity - b.opacity) > 0.0001) return b.opacity - a.opacity;
      return b.order - a.order;
    })
    .slice(0, limit);

  return new Set(selected.map((candidate) => candidate.key));
}

function isOverlayVideoLayer(layer: unknown): layer is FxOverlayVideoLayer {
  if (!layer || typeof layer !== "object") return false;
  const typed = layer as FxOverlayVideoLayer;
  return typed.type === "overlay-video" && typeof typed.id === "string" && typeof typed.src === "string";
}

function ensureLegacyOverlayEntry(args: {
  key: string;
  elementId: string;
  packet: FxRenderPacket;
  layer: FxOverlayVideoLayer;
  node: Konva.Node;
  parent: Konva.Container;
  overlayMap: FxVideoOverlayMap;
  size: { width: number; height: number };
}): LegacyFxVideoOverlayEntry | null {
  const { key, elementId, packet, layer, node, parent, overlayMap, size } = args;
  let existing = overlayMap.get(key);
  let entry = existing?.kind === "legacy" ? existing : null;

  if (!entry || entry.sourceNode !== node || !entry.overlayNode.getParent() || entry.src !== layer.src) {
    if (existing) removeFxVideoOverlay(key, overlayMap);
    entry = createLegacyOverlayEntry({
      key,
      elementId,
      packet,
      layer,
      node,
      parent,
      size,
    });
    if (!entry) return null;
    overlayMap.set(key, entry);
    return entry;
  }

  if (entry.size.width !== size.width || entry.size.height !== size.height) {
    entry.size = size;
    entry.overlayNode.width(size.width);
    entry.overlayNode.height(size.height);
  }
  return entry;
}

function createLegacyOverlayEntry(args: {
  key: string;
  elementId: string;
  packet: FxRenderPacket;
  layer: FxOverlayVideoLayer;
  node: Konva.Node;
  parent: Konva.Container;
  size: { width: number; height: number };
}): LegacyFxVideoOverlayEntry | null {
  const { key, elementId, packet, layer, node, parent, size } = args;
  const video = createOverlayVideoElement(layer.src);
  const overlayNode = new Konva.Image({
    id: `fx-overlay-video-${packet.fxBlockId}-${layer.id}`,
    name: "vb-fx-overlay-video",
    listening: false,
    draggable: false,
    image: video,
    visible: false,
    width: size.width,
    height: size.height,
  });
  parent.add(overlayNode);

  const entry: LegacyFxVideoOverlayEntry = {
    kind: "legacy",
    key,
    elementId,
    fxBlockId: packet.fxBlockId,
    layerId: layer.id,
    src: layer.src,
    sourceNode: node,
    overlayNode,
    video,
    metadataLoaded: false,
    failed: false,
    warned: false,
    size,
    dragging: false,
    editBound: false,
    currentLayer: { ...layer },
    onCanvasEditCommit: null,
    onDragStart: () => {
      entry.dragging = true;
    },
    onDragEnd: () => {
      entry.dragging = false;
      const layerPlacement =
        entry.currentLayer.placementMode === "screen-space" ? "screen-space" : "follow-target";
      const nextPositionX =
        layerPlacement === "screen-space"
          ? entry.overlayNode.x()
          : entry.overlayNode.x() - entry.sourceNode.x();
      const nextPositionY =
        layerPlacement === "screen-space"
          ? entry.overlayNode.y()
          : entry.overlayNode.y() - entry.sourceNode.y();
      entry.onCanvasEditCommit?.({
        fxBlockId: entry.fxBlockId,
        layerId: entry.layerId,
        positionX: clamp(toFinite(nextPositionX, 0), -4000, 4000),
        positionY: clamp(toFinite(nextPositionY, 0), -4000, 4000),
      });
    },
    onLoadedMetadata: () => {
      entry.metadataLoaded = true;
    },
    onError: () => {
      entry.failed = true;
      if (entry.warned) return;
      entry.warned = true;
      console.warn(
        `[FX Overlay Video] failed to load "${entry.src}" (block=${entry.fxBlockId}, layer=${entry.layerId})`
      );
    },
  };

  entry.overlayNode.on("dragstart.vbFxOverlayEdit", entry.onDragStart);
  entry.overlayNode.on("dragend.vbFxOverlayEdit", entry.onDragEnd);
  entry.video.addEventListener("loadedmetadata", entry.onLoadedMetadata);
  entry.video.addEventListener("error", entry.onError);
  try {
    entry.video.load();
  } catch {}
  return entry;
}

function ensureBatchOverlayEntry(args: {
  key: string;
  elementId: string;
  node: Konva.Node;
  parent: Konva.Container;
  overlayMap: FxVideoOverlayMap;
  size: { width: number; height: number };
  renderScale: number;
}): BatchFxVideoOverlayEntry | null {
  const { key, elementId, node, parent, overlayMap, size } = args;
  let existing = overlayMap.get(key);
  let entry = existing?.kind === "batch" ? existing : null;
  const nextRenderScale = clamp(toFinite(args.renderScale, 1), MIN_BATCH_RENDER_SCALE, MAX_BATCH_RENDER_SCALE);

  if (!entry || entry.sourceNode !== node || !entry.overlayNode.getParent()) {
    if (existing) removeFxVideoOverlay(key, overlayMap);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const overlayNode = new Konva.Image({
      id: `fx-overlay-video-batch-${elementId}`,
      name: "vb-fx-overlay-video-batch",
      image: canvas,
      listening: false,
      draggable: false,
      visible: false,
      width: size.width,
      height: size.height,
    });
    parent.add(overlayNode);
    entry = {
      kind: "batch",
      key,
      elementId,
      sourceNode: node,
      overlayNode,
      canvas,
      ctx,
      size,
      renderScale: nextRenderScale,
      layers: new Map<string, FxVideoBatchLayerEntry>(),
    };
    overlayMap.set(key, entry);
    return entry;
  }

  entry.size = size;
  entry.renderScale = nextRenderScale;
  return entry;
}

function ensureBatchLayerEntry(
  entry: BatchFxVideoOverlayEntry,
  candidate: OverlayLayerCandidate
): FxVideoBatchLayerEntry | null {
  const { key, packet, layer } = candidate;
  let layerEntry = entry.layers.get(key) ?? null;
  if (!layerEntry || layerEntry.src !== layer.src) {
    if (layerEntry) disposeBatchLayerEntry(layerEntry);
    layerEntry = createBatchLayerEntry({
      key,
      fxBlockId: packet.fxBlockId,
      layerId: layer.id,
      src: layer.src,
      layer,
    });
    if (!layerEntry) return null;
    entry.layers.set(key, layerEntry);
  } else {
    layerEntry.currentLayer = { ...layer };
  }
  return layerEntry;
}

function createBatchLayerEntry(args: {
  key: string;
  fxBlockId: string;
  layerId: string;
  src: string;
  layer: FxOverlayVideoLayer;
}): FxVideoBatchLayerEntry | null {
  const { key, fxBlockId, layerId, src, layer } = args;
  const video = createOverlayVideoElement(src);
  const layerEntry: FxVideoBatchLayerEntry = {
    key,
    fxBlockId,
    layerId,
    src,
    video,
    metadataLoaded: false,
    failed: false,
    warned: false,
    currentLayer: { ...layer },
    onLoadedMetadata: () => {
      layerEntry.metadataLoaded = true;
    },
    onError: () => {
      layerEntry.failed = true;
      if (layerEntry.warned) return;
      layerEntry.warned = true;
      console.warn(
        `[FX Overlay Video] failed to load "${layerEntry.src}" (block=${layerEntry.fxBlockId}, layer=${layerEntry.layerId})`
      );
    },
  };
  layerEntry.video.addEventListener("loadedmetadata", layerEntry.onLoadedMetadata);
  layerEntry.video.addEventListener("error", layerEntry.onError);
  try {
    layerEntry.video.load();
  } catch {}
  return layerEntry;
}

function cleanupBatchLayers(entry: BatchFxVideoOverlayEntry, keepLayerKeys: Set<string>): boolean {
  let changed = false;
  for (const key of entry.layers.keys()) {
    if (keepLayerKeys.has(key)) continue;
    const layerEntry = entry.layers.get(key);
    if (!layerEntry) continue;
    disposeBatchLayerEntry(layerEntry);
    entry.layers.delete(key);
    changed = true;
  }
  return changed;
}

function disposeBatchLayerEntry(layerEntry: FxVideoBatchLayerEntry): void {
  try {
    layerEntry.video.removeEventListener("loadedmetadata", layerEntry.onLoadedMetadata);
    layerEntry.video.removeEventListener("error", layerEntry.onError);
  } catch {}
  try {
    layerEntry.video.pause();
    layerEntry.video.removeAttribute("src");
    layerEntry.video.load();
  } catch {}
}

function drawBatchOverlay(entry: BatchFxVideoOverlayEntry, drawCandidates: BatchDrawCandidate[]): void {
  const renderScale = clamp(entry.renderScale, MIN_BATCH_RENDER_SCALE, MAX_BATCH_RENDER_SCALE);
  const renderWidth = Math.max(2, Math.round(entry.size.width * renderScale));
  const renderHeight = Math.max(2, Math.round(entry.size.height * renderScale));

  if (entry.canvas.width !== renderWidth || entry.canvas.height !== renderHeight) {
    entry.canvas.width = renderWidth;
    entry.canvas.height = renderHeight;
  }

  const ctx = entry.ctx;
  ctx.clearRect(0, 0, renderWidth, renderHeight);

  const sorted = drawCandidates.slice().sort((a, b) => a.zOffset - b.zOffset);
  for (const candidate of sorted) {
    const layer = candidate.layerEntry.currentLayer;
    const video = candidate.layerEntry.video;
    // Skip if not decoded or seek is still in flight — prevents the stale-frame flash on re-entry.
    if (video.readyState < 2 || video.seeking) continue;

    const composite = resolveFxBlendComposite(layer.blendMode) as GlobalCompositeOperation;
    const depthScale = resolveOverlayDepthScale(layer);
    const positionX = clamp(toFinite(layer.positionX, 0), -4000, 4000) * renderScale;
    const positionY = clamp(toFinite(layer.positionY, 0), -4000, 4000) * renderScale;

    ctx.save();
    ctx.globalAlpha = clamp01(candidate.opacity);
    ctx.globalCompositeOperation = composite;
    ctx.translate(renderWidth * 0.5 + positionX, renderHeight * 0.5 + positionY);
    ctx.scale(depthScale, depthScale);
    ctx.translate(renderWidth * -0.5, renderHeight * -0.5);
    try {
      ctx.drawImage(video, 0, 0, renderWidth, renderHeight);
    } catch {}
    ctx.restore();
  }
}

function syncOverlayVideoPlayback(args: {
  video: HTMLVideoElement;
  metadataLoaded: boolean;
  playbackTimeSec: number;
  playbackRate: number;
  isPlaying?: boolean;
}): boolean {
  const { video, metadataLoaded, playbackTimeSec, playbackRate, isPlaying = false } = args;
  let changed = false;
  if (Math.abs(video.playbackRate - playbackRate) > 0.0001) {
    try {
      video.playbackRate = playbackRate;
    } catch {}
  }

  const readyForSeek = metadataLoaded || video.readyState >= 1;
  if (readyForSeek) {
    const delta = Math.abs(video.currentTime - playbackTimeSec);
    // Lower threshold to 1/60 (16ms) so scrub seeks are issued promptly,
    // reducing the chance of the overlay drawing a stale frame.
    if (!Number.isFinite(video.currentTime) || delta > 1 / 60) {
      try {
        video.currentTime = playbackTimeSec;
        changed = true;
      } catch {}
    }
  }

  // Only play the overlay video when the editor is actively playing.
  // During scrub/pause, keep overlays paused so they don't advance independently
  // and cause a flash when drawImage() captures a mid-advance frame.
  if (isPlaying) {
    if (video.paused && video.readyState >= 2) {
      const playResult = video.play();
      if (playResult && typeof playResult.catch === "function") {
        playResult.catch(() => {});
      }
    }
  } else {
    if (!video.paused) {
      try { video.pause(); } catch {}
    }
  }
  return changed;
}

function resolveOverlayPlaybackTime(args: {
  packet: FxRenderPacket;
  layer: FxOverlayVideoLayer;
  videoDuration: number;
}): { active: boolean; timeSec: number } {
  const { packet, layer, videoDuration } = args;
  const segmentElapsed = Math.max(0, toFinite(packet.durationSec, 0) * clamp01(packet.progress));
  const startOffset = Math.max(0, toFinite(layer.startOffset, 0));
  const playbackRate = clamp(toFinite(layer.playbackRate, 1), 0.1, 4);
  const requestedDuration = Math.max(0.001, toFinite(layer.duration, 1));
  const availableDuration = Number.isFinite(videoDuration)
    ? Math.max(0, videoDuration - startOffset)
    : Number.POSITIVE_INFINITY;
  if (availableDuration <= 0) return { active: false, timeSec: startOffset };

  const effectiveDuration = Number.isFinite(availableDuration)
    ? Math.max(0.001, Math.min(requestedDuration, availableDuration))
    : requestedDuration;
  const sourceElapsed = segmentElapsed * playbackRate;
  if (!layer.loop && sourceElapsed >= effectiveDuration) {
    return { active: false, timeSec: startOffset + effectiveDuration };
  }
  const normalized = layer.loop ? sourceElapsed % effectiveDuration : Math.min(sourceElapsed, effectiveDuration);
  let timeSec = startOffset + normalized;
  if (Number.isFinite(videoDuration)) {
    timeSec = clamp(timeSec, 0, Math.max(0, videoDuration - 0.001));
  }
  return { active: true, timeSec };
}

function resolveNodeSize(node: Konva.Node, layer: FxOverlayVideoLayer): { width: number; height: number } {
  const nodeAny = node as any;
  const width = Number(nodeAny.width?.() ?? 0);
  const height = Number(nodeAny.height?.() ?? 0);
  if (Number.isFinite(width) && width > 1 && Number.isFinite(height) && height > 1) {
    return { width: Math.round(width), height: Math.round(height) };
  }

  const image = typeof nodeAny.image === "function" ? nodeAny.image() : null;
  const sourceWidth =
    (image as HTMLVideoElement | null)?.videoWidth ||
    (image as HTMLImageElement | null)?.naturalWidth ||
    (image as HTMLCanvasElement | null)?.width ||
    0;
  const sourceHeight =
    (image as HTMLVideoElement | null)?.videoHeight ||
    (image as HTMLImageElement | null)?.naturalHeight ||
    (image as HTMLCanvasElement | null)?.height ||
    0;
  if (sourceWidth > 1 && sourceHeight > 1) {
    return { width: Math.round(sourceWidth), height: Math.round(sourceHeight) };
  }

  const fallbackRect =
    typeof nodeAny.getClientRect === "function" ? nodeAny.getClientRect({ skipTransform: true }) : null;
  if (fallbackRect && Number.isFinite(fallbackRect.width) && Number.isFinite(fallbackRect.height)) {
    return {
      width: Math.max(1, Math.round(fallbackRect.width)),
      height: Math.max(1, Math.round(fallbackRect.height)),
    };
  }

  return {
    width: Math.max(1, Math.round(toFinite(layer.duration, 1) * 640)),
    height: Math.max(1, Math.round(toFinite(layer.duration, 1) * 360)),
  };
}

function configureOverlayCanvasEdit(args: {
  entry: LegacyFxVideoOverlayEntry;
  enabled: boolean;
  layer: FxOverlayVideoLayer;
  onCanvasEditCommit?: (next: FxVideoOverlayCanvasEditCommit) => void;
}): boolean {
  const { entry, enabled, layer, onCanvasEditCommit } = args;
  let changed = false;
  entry.currentLayer = { ...layer };
  entry.onCanvasEditCommit = onCanvasEditCommit ?? null;

  if (entry.overlayNode.draggable() !== enabled) {
    entry.overlayNode.draggable(enabled);
    changed = true;
  }
  if (entry.overlayNode.listening() !== enabled) {
    entry.overlayNode.listening(enabled);
    changed = true;
  }
  if (!enabled && entry.dragging) {
    entry.dragging = false;
    try {
      entry.overlayNode.stopDrag();
    } catch {}
    changed = true;
  }
  entry.editBound = enabled;
  return changed;
}

function syncOverlayTransform(overlay: Konva.Image, source: Konva.Node, layer: FxOverlayVideoLayer): void {
  const overlayAny = overlay as any;
  const sourceAny = source as any;
  const positionX = clamp(toFinite(layer.positionX, 0), -4000, 4000);
  const positionY = clamp(toFinite(layer.positionY, 0), -4000, 4000);
  const depthScale = resolveOverlayDepthScale(layer);
  const placementMode = layer.placementMode === "screen-space" ? "screen-space" : "follow-target";
  if (placementMode === "screen-space") {
    overlay.x(positionX);
    overlay.y(positionY);
    overlay.scaleX(depthScale);
    overlay.scaleY(depthScale);
    overlay.rotation(0);
    overlay.offsetX(0);
    overlay.offsetY(0);
    if (typeof overlayAny.skewX === "function") overlayAny.skewX(0);
    if (typeof overlayAny.skewY === "function") overlayAny.skewY(0);
    return;
  }

  overlay.x(source.x() + positionX);
  overlay.y(source.y() + positionY);
  overlay.scaleX(source.scaleX() * depthScale);
  overlay.scaleY(source.scaleY() * depthScale);
  overlay.rotation(source.rotation());
  overlay.offsetX(source.offsetX());
  overlay.offsetY(source.offsetY());
  if (typeof overlayAny.skewX === "function" && typeof sourceAny.skewX === "function") {
    overlayAny.skewX(sourceAny.skewX());
  }
  if (typeof overlayAny.skewY === "function" && typeof sourceAny.skewY === "function") {
    overlayAny.skewY(sourceAny.skewY());
  }
}

function syncBatchOverlayTransform(overlay: Konva.Image, source: Konva.Node): void {
  const overlayAny = overlay as any;
  const sourceAny = source as any;
  overlay.x(source.x());
  overlay.y(source.y());
  overlay.scaleX(source.scaleX());
  overlay.scaleY(source.scaleY());
  overlay.rotation(source.rotation());
  overlay.offsetX(source.offsetX());
  overlay.offsetY(source.offsetY());
  overlay.visible(source.visible());
  overlay.listening(false);
  overlay.draggable(false);
  if (typeof overlayAny.skewX === "function" && typeof sourceAny.skewX === "function") {
    overlayAny.skewX(sourceAny.skewX());
  }
  if (typeof overlayAny.skewY === "function" && typeof sourceAny.skewY === "function") {
    overlayAny.skewY(sourceAny.skewY());
  }
}

function placeOverlayAbove(args: {
  parent: Konva.Container;
  source: Konva.Node;
  overlay: Konva.Node;
  zOffset?: number;
}): void {
  const { parent, source, overlay, zOffset = 0 } = args;
  const nextIndex = clamp(source.getZIndex() + 1 + zOffset, 0, Math.max(0, parent.getChildren().length - 1));
  if (overlay.getZIndex() !== nextIndex) {
    overlay.zIndex(nextIndex);
  }
}

function resolveOverlayDepthScale(layer: FxOverlayVideoLayer): number {
  const z = clamp(toFinite(layer.positionZ, 0), -400, 400);
  return clamp(1 + z / 100, 0.05, 8);
}

function resolveOverlayZIndexOffset(layer: FxOverlayVideoLayer): number {
  const z = clamp(toFinite(layer.positionZ, 0), -400, 400);
  return Math.round(z / 100);
}

function createOverlayVideoElement(src: string): HTMLVideoElement {
  const video = document.createElement("video");
  video.muted = true;
  video.loop = false;
  video.autoplay = false;
  video.playsInline = true;
  video.preload = "metadata";
  video.crossOrigin = "anonymous";
  video.src = src;
  return video;
}

function getBatchOverlayKey(elementId: string): string {
  return `${elementId}::${BATCH_KEY_SUFFIX}`;
}

function cleanupElementLegacyOverlays(elementId: string, overlayMap: FxVideoOverlayMap): boolean {
  let changed = false;
  const prefix = `${elementId}::`;
  for (const [key, entry] of overlayMap.entries()) {
    if (!key.startsWith(prefix)) continue;
    if (entry.kind !== "legacy") continue;
    changed = removeFxVideoOverlay(key, overlayMap) || changed;
  }
  return changed;
}

function removeFxVideoOverlay(key: string, overlayMap: FxVideoOverlayMap): boolean {
  const entry = overlayMap.get(key);
  if (!entry) return false;
  if (entry.kind === "legacy") {
    removeLegacyOverlayEntry(entry);
  } else {
    removeBatchOverlayEntry(entry);
  }
  overlayMap.delete(key);
  return true;
}

function removeLegacyOverlayEntry(entry: LegacyFxVideoOverlayEntry): void {
  try {
    entry.video.removeEventListener("loadedmetadata", entry.onLoadedMetadata);
    entry.video.removeEventListener("error", entry.onError);
  } catch {}
  try {
    entry.overlayNode.off("dragstart.vbFxOverlayEdit", entry.onDragStart);
    entry.overlayNode.off("dragend.vbFxOverlayEdit", entry.onDragEnd);
  } catch {}
  try {
    entry.video.pause();
    entry.video.removeAttribute("src");
    entry.video.load();
  } catch {}
  try {
    entry.overlayNode.destroy();
  } catch {}
}

function removeBatchOverlayEntry(entry: BatchFxVideoOverlayEntry): void {
  for (const layerEntry of entry.layers.values()) {
    disposeBatchLayerEntry(layerEntry);
  }
  entry.layers.clear();
  try {
    entry.overlayNode.destroy();
  } catch {}
}

function hideOverlayNode(node: Konva.Node): boolean {
  if (!node.visible()) return false;
  node.visible(false);
  return true;
}

function pauseOverlayVideo(video: HTMLVideoElement): void {
  if (video.paused) return;
  try {
    video.pause();
  } catch {}
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function toFinite(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}
